/* eslint-disable @typescript-eslint/no-explicit-any */
import colors from '@theme/colors'
import { ScheduleContext } from './context'
import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  useSchedule,
  useSearchEvents,
} from '@services/nomos_api/resources/search/events'

import { GetAllSavedEventsProps } from '@services/nomos_api/resources/saved_event/getAll'
import { SCHEDULE_TABS } from '@containers/EventsCalendar/types'
import { SCHEDULE_FILTER } from './components/EventFilters/types'

type ScheduleControllerProps = {
  children: React.ReactChild
}

export default function ScheduleController({
  children,
}: ScheduleControllerProps) {
  const [selectedDate, setSelectedDate] = useState(new Date())
  const [eventParams, setEventParams] = useState<GetAllSavedEventsProps>({})
  const [scheduleParams, setScheduleParams] = useState({} as any)
  const [tab, setTab] = useState(SCHEDULE_TABS.MINHA_AGENDA)
  const [textSearch, setTextSearch] = useState('')
  const [activeFilter, setActiveFilter] = useState<number>(
    SCHEDULE_FILTER.UNDEFINED
  )
  const [slim, setSlim] = useState<boolean>(
    () => typeof window !== 'undefined' && window.innerWidth < 1200
  )

  useEffect(() => {
    if (typeof window === 'undefined') {
      return
    }
    window.addEventListener('resize', () => {
      setSlim(window.innerWidth < 1200)
    })
  }, [])

  const handleUpdateFilters = useCallback((filters: any) => {
    setEventParams((prevState: any) => ({ ...prevState, ...filters }))
  }, [])

  const handleUpdateSchedule = useCallback((filters: any) => {
    setScheduleParams((prevState: any) => ({ ...prevState, ...filters }))
  }, [])

  const handleUpdateCalendar = useCallback(
    (calendarParams: Record<string, string>) => {
      const fromDate = calendarParams['buckets.events.filter.date.from']
      const toDate = calendarParams['buckets.events.filter.date.to']
      setTextSearch(calendarParams['buckets.events.filter.text'])

      handleUpdateFilters({
        date: {
          from: fromDate,
          to: toDate,
        },
      })
      handleUpdateSchedule({
        date: {
          from: fromDate,
          to: toDate,
        },
      })
    },
    []
  )

  useEffect(() => {
    handleUpdateFilters({ mode: tab })
  }, [tab])

  const { data: events, isFetching: isLoadingEvents } =
    useSearchEvents(eventParams)

  const { data: schedules, isFetching: isLoadingSchedules } =
    useSchedule(scheduleParams)

  let eventsToList = [
    ...(events?.results || []).map((r: any) => {
      const hasUserEvents = r.userEvents?.length > 0

      return {
        ...r,
        borderColor: colors['$blue1-medium-dark'],
        indicatorColor: hasUserEvents
          ? colors['$estendidas-azul-color-extended-blue-1']
          : colors['$estendidas-verde-color-extended-green-1'],
        background: hasUserEvents
          ? colors['$estendidas-azul-color-extended-blue-2']
          : colors['$estendidas-verde-color-extended-green-2'],
        shouldHighlight: Boolean(eventParams.panel_monitors),
      }
    }),
    ...(schedules?.results || []).map((r: any) => {
      const hasUserEvents = r.userEvents?.length > 0
      return {
        ...r,
        borderColor: colors['$color-border-secondary'],
        indicatorColor: hasUserEvents
          ? colors['$estendidas-azul-color-extended-blue-1']
          : colors['$estendidas-verde-color-extended-green-1'],
        background: hasUserEvents
          ? colors['$estendidas-azul-color-extended-blue-2']
          : colors['$estendidas-verde-color-extended-green-2'],
        shouldHighlight: Boolean(eventParams.panel_monitors),
      }
    }),
  ]

  eventsToList = eventsToList.filter((e) => {
    let ret = true
    if (eventParams.mode === 3) ret = ret && e.openDataResource === 'eagendas'
    if (eventParams.mode === 1)
      ret = ret && (e.userEvents as Array<any>).length > 0
    if (eventParams.panel_propositions === 'true')
      ret = ret && (e.savedPropositions as Array<any>).length > 0
    if (textSearch)
      ret =
        ret && e.description?.toLowerCase().includes(textSearch.toLowerCase())
    return ret
  })

  const store = useMemo(
    () => ({
      events: eventsToList,
      isLoading: isLoadingEvents || isLoadingSchedules,
      handleUpdateFilters,
      handleUpdateSchedule,
      handleUpdateCalendar,
      selectedDate,
      setSelectedDate,
      tab,
      setTab,
      activeFilter,
      setActiveFilter,
      slim,
    }),
    [
      eventsToList,
      isLoadingEvents,
      isLoadingSchedules,
      handleUpdateFilters,
      handleUpdateSchedule,
      handleUpdateCalendar,
      selectedDate,
      setSelectedDate,
      tab,
      setTab,
      activeFilter,
      setActiveFilter,
      slim,
    ]
  )

  return (
    <ScheduleContext.Provider value={store}>
      {children}
    </ScheduleContext.Provider>
  )
}
